import React, { useCallback } from 'react';

import { useCurrentTheme } from 'app/shared/theme';
import { ACCESSIBILITY_HELP_ARTICLE_URL } from 'app/shared/utils/siteUrls';
import { PropertyMap } from 'app/shared/typings';
import {
  Box,
  NavigationLink,
  Typography,
  ZendeskLink,
} from 'app/shared/components/atoms';
import { MappingsProps } from 'app/fan/components/templates/EventPage/components/EventCharacteristicsList/utils';
import { ReactComponent as UniversalAccess } from 'icons/universal-access.svg';

interface AccessibilityProps {
  propertyMap?: PropertyMap;
}

export function useAccessibilityMappings({ formatMessage }: MappingsProps) {
  const { colors } = useCurrentTheme();
  const intlPrefix = 'event.eventCharacteristicsList.accessibility';

  const accessibleEntranceAvailableMessage = formatMessage(
    { id: `${intlPrefix}.accessibleEntranceAvailable` },
    {
      a: (chunks) => <ZendeskLink>{chunks}</ZendeskLink>,
    }
  );
  const moreInfoMessage = formatMessage(
    { id: `${intlPrefix}.moreInfo` },
    {
      a: (chunks) => (
        <NavigationLink
          variant="primary"
          openInNewTab
          href={ACCESSIBILITY_HELP_ARTICLE_URL}
        >
          {chunks}
        </NavigationLink>
      ),
    }
  );
  const defaultMessage = formatMessage(
    { id: `${intlPrefix}.defaultDetails` },
    {
      a: (chunks) => <ZendeskLink>{chunks}</ZendeskLink>,
    }
  );

  return useCallback(({ propertyMap }: AccessibilityProps) => {
    return {
      narrowDoorwaysNoStairs: {
        id: 'accessibility',
        title: formatMessage({ id: `${intlPrefix}.accessibilityInfo` }),
        icon: UniversalAccess,
        details: (
          <Box gap={3} flexDirection="column">
            <Typography color={colors.primary.white.base}>
              {formatMessage({
                id: `${intlPrefix}.narrowDoorwaysNoStairs`,
              })}
            </Typography>
            {propertyMap?.accessibleEntranceAvailable && (
              <Typography color={colors.primary.white.base}>
                {accessibleEntranceAvailableMessage}
              </Typography>
            )}
            <Typography color={colors.primary.white.base}>
              {moreInfoMessage}
            </Typography>
            <Typography color={colors.primary.white.base}>
              {defaultMessage}
            </Typography>
          </Box>
        ),
      },
      notWheelchairAccessible: {
        id: 'accessibility',
        title: formatMessage({ id: `${intlPrefix}.accessibilityInfo` }),
        icon: UniversalAccess,
        details: (
          <Box gap={3} flexDirection="column">
            <Typography color={colors.primary.white.base}>
              {formatMessage({ id: `${intlPrefix}.notWheelchairAccessible` })}
            </Typography>
            {propertyMap?.accessibleEntranceAvailable && (
              <Typography color={colors.primary.white.base}>
                {accessibleEntranceAvailableMessage}
              </Typography>
            )}
            <Typography color={colors.primary.white.base}>
              {moreInfoMessage}
            </Typography>
            <Typography color={colors.primary.white.base}>
              {defaultMessage}
            </Typography>
          </Box>
        ),
      },
      wheelchairAccessibleNoStairs: {
        id: 'accessibility',
        title: formatMessage({ id: `${intlPrefix}.accessibilityInfo` }),
        icon: UniversalAccess,
        details: (
          <Box gap={3} flexDirection="column">
            <Typography color={colors.primary.white.base}>
              {formatMessage({
                id: `${intlPrefix}.wheelchairAccessibleNoStairs`,
              })}
            </Typography>
            {propertyMap?.accessibleEntranceAvailable && (
              <Typography color={colors.primary.white.base}>
                {accessibleEntranceAvailableMessage}
              </Typography>
            )}
            <Typography color={colors.primary.white.base}>
              {moreInfoMessage}
            </Typography>
            <Typography color={colors.primary.white.base}>
              {defaultMessage}
            </Typography>
          </Box>
        ),
      },
      wheelchairAccessibleWithStairs: {
        id: 'accessibility',
        title: formatMessage({ id: `${intlPrefix}.accessibilityInfo` }),
        icon: UniversalAccess,
        details: (
          <Box gap={3} flexDirection="column">
            <Typography color={colors.primary.white.base}>
              {formatMessage({
                id: `${intlPrefix}.wheelchairAccessibleWithStairs`,
              })}
            </Typography>
            {propertyMap?.accessibleEntranceAvailable && (
              <Typography color={colors.primary.white.base}>
                {accessibleEntranceAvailableMessage}
              </Typography>
            )}
            <Typography color={colors.primary.white.base}>
              {moreInfoMessage}
            </Typography>
            <Typography color={colors.primary.white.base}>
              {defaultMessage}
            </Typography>
          </Box>
        ),
      },
      default: {
        id: 'accessibility',
        title: formatMessage({ id: `${intlPrefix}.accessibilityInfo` }),
        icon: UniversalAccess,
        details: (
          <Box gap={3} flexDirection="column">
            {propertyMap?.accessibleEntranceAvailable && (
              <>
                <Typography color={colors.primary.white.base}>
                  {accessibleEntranceAvailableMessage}
                </Typography>
                <Typography color={colors.primary.white.base}>
                  {moreInfoMessage}
                </Typography>
              </>
            )}
            <Typography color={colors.primary.white.base}>
              {defaultMessage}
            </Typography>
          </Box>
        ),
      },
    };
  }, []);
}
